import React, { useContext } from 'react';
import {
  Composites,
  CustomScroll,
  NotificationBanner,
  Thumbnails,
} from '@wix/wix-base-ui';
import type { AnimationsListProps, AnimationThumbnailProps } from '../types';
import styles from '../assets/styles.scss';
import { AnimationTumbnail } from '../components/AnimationThumbnail';
import { TranslationContext } from '../context/TranslationContext';

/**
 * Create a Thumbnails list from animations presets
 */
export const AnimationList: React.FC<AnimationsListProps> = ({
  animations,
  value = { name: '' },
  onChange,
  onClickPreview,
  onHoverPreview,
  onPreviewStop,
  thumbs,
  sortOrder = [],
  disabled,
  message,
  trigger,
}) => {
  const getDuration = React.useCallback(
    () => value.params?.duration ?? 0,
    [value],
  );

  const animationThumbnailProps: Omit<
    AnimationThumbnailProps,
    'selected' | 'thumb' | 'ref'
  > = onClickPreview
    ? {
        width: 72,
        height: 72,
        getDuration,
        onClickPreview: () => {
          onPreviewStop?.(trigger);
          onClickPreview();
        },
        onPreviewStop: () => onPreviewStop?.(trigger),
        showPlayIconOnSelected: true,
      }
    : {
        width: 72,
        height: 72,
        showPlayIconOnSelected: false,
      };

  const thumbRef = React.useRef<HTMLDivElement>(null);
  const { translate } = useContext(TranslationContext);

  const options = animations
    // Sort by sortOrder if provided or by default order
    .sort((a, b) => {
      const aIndex = sortOrder.indexOf(a.name);
      const bIndex = sortOrder.indexOf(b.name);
      return aIndex < 0 ? 1 : aIndex - bIndex;
    })
    .map(({ name }, i) => {
      const selected = name === value.name;
      const thumb = thumbs[name as keyof typeof thumbs];
      return {
        value: name,
        label: translate(`name_${name.toLowerCase()}`),
        illustration: (
          <AnimationTumbnail
            {...animationThumbnailProps}
            onHoverPreview={
              onHoverPreview
                ? () => {
                    onPreviewStop?.(trigger);
                    onHoverPreview(name);
                  }
                : undefined
            }
            selected={selected}
            thumb={thumb}
            ref={selected ? thumbRef : undefined}
          />
        ),
        key: `thumb${i}`,
      };
    });

  React.useEffect(() => {
    thumbRef.current?.scrollIntoView({
      behavior: 'auto',
      block: 'center',
    });
  }, [thumbRef]);

  React.useEffect(() => {
    return () => {
      onPreviewStop?.(trigger);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.animationList}>
      <CustomScroll allowOuterScroll>
        {message ? (
          <div className={styles.notificationBannerWrapper}>
            <NotificationBanner
              type="floating"
              skin="standard"
              multiline
              text={message.text}
              link={message.link}
              shouldTranslate={false}
            />
          </div>
        ) : null}
        <Composites.Thumbnails>
          <Thumbnails
            disabled={disabled}
            value={value.name}
            onChange={onChange}
            options={options}
            maxThumbsPerRow={3}
            shouldTranslate={false}
            noneThumbnailProps={{
              value: 'None',
              label: translate('name_none'),
              height: 72,
            }}
          />
        </Composites.Thumbnails>
      </CustomScroll>
    </div>
  );
};
